import ProcessingExecutionMode from '../../processing/models/ProcessingExecutionMode';
import {ParentReportJob} from '../models/ParentReportJob';
import {ProducerReportJob} from '../models/ProducerReportJob';
import {ReportConfiguration} from '../models/ReportConfigurationModel';
import ReportJob from '../models/ReportJob';
import ReportJobStatus from '../models/ReportJobStatus';
import ReportJobType from '../models/ReportJobType';

export const LOAD_REPORTS_CONFIG = 'LOAD_REPORTS_CONFIG';
export interface LoadReportsConfigAction {
  type: typeof LOAD_REPORTS_CONFIG;
}

export const LOAD_REPORTS_CONFIG_SUCCESS = 'LOAD_REPORTS_CONFIG_SUCCESS';
export interface LoadReportsConfigSuccessAction {
  type: typeof LOAD_REPORTS_CONFIG_SUCCESS;
  payload: {[key: string]: ReportConfiguration};
}

export const LOAD_REPORTS = 'LOAD_REPORTS';
export interface LoadReportsAction {
  type: typeof LOAD_REPORTS;
  payload: {
    campaignId: number;
    type: ReportJobType;
    start_date?: string;
    end_date?: string;
    producer_ref?: string;
    status?: ReportJobStatus[];
    limit?: number;
    date?: Date;
  };
}

export const LOAD_REPORTS_SUCCESS = 'LOAD_REPORTS_SUCCESS';
export interface LoadReportsSuccessAction {
  type: typeof LOAD_REPORTS_SUCCESS;
  payload: {[key: string]: ReportJob};
}

export const SET_SELECTED_REPORT = 'SET_SELECTED_REPORT';
export interface SetSelectedReportAction {
  type: typeof SET_SELECTED_REPORT;
  payload: ReportJob;
}

export const RETRY_REPORT_GENERATION = 'RETRY_REPORT_GENERATION';
export interface RetryReportGenerationAction {
  type: typeof RETRY_REPORT_GENERATION;
  payload: {
    reportId: string;
  };
}

export const GENERATE_NOTIFICATION = 'GENERATE_NOTIFICATION';
export interface GenerateNotificationAction {
  type: typeof GENERATE_NOTIFICATION;
  payload: {
    reportJob: ReportJob;
  };
}

export const GET_PRODUCER_REPORT_JOB = 'GET_PRODUCER_REPORT_JOB';
export interface GetProducerReportJobAction {
  type: typeof GET_PRODUCER_REPORT_JOB;
  payload: {
    campaignId: number;
    type: ReportJobType;
    start_date?: string;
    end_date?: string;
  };
}

export const GET_PRODUCER_REPORT_JOB_SUCCESS =
  'GET_PRODUCER_REPORT_JOB_SUCCESS';
export interface GetProducerReportJobSuccessAction {
  type: typeof GET_PRODUCER_REPORT_JOB_SUCCESS;
  payload: {
    [producer_ref: string]: ProducerReportJob;
  };
}
export const GET_PARENT_REPORT_JOBS = 'GET_PARENT_REPORT_JOBS';
export interface GetParentReportJobsAction {
  type: typeof GET_PARENT_REPORT_JOBS;
  payload: {
    campaignId: number;
    type: ReportJobType;
    start_date?: string;
    end_date?: string;
  };
}

export const GET_PARENT_REPORT_JOBS_SUCCESS = 'GET_PARENT_REPORT_JOBS_SUCCESS';
export interface GetParentReportJobsSuccessAction {
  type: typeof GET_PARENT_REPORT_JOBS_SUCCESS;
  payload: {
    [key: string]: ParentReportJob;
  };
}

export const GENERATE_CAMPAIGN_REPORT = 'GENERATE_CAMPAIGN_REPORT';
export interface GenerateCampaignReportAction {
  type: typeof GENERATE_CAMPAIGN_REPORT;
  payload: {
    campaignId: string;
    type: ReportJobType;
    producers: string[];
    mode?: ProcessingExecutionMode;
  };
}

export const DOWNLOAD_ZIP_FILE = 'DOWNLOAD_ZIP_FILE';
export interface DownloadZipFileAction {
  type: typeof DOWNLOAD_ZIP_FILE;
  payload: {
    parentJobs: string[];
  };
}

export const GENERATE_REPORT = 'GENERATE_REPORT';
export interface GenerateReportAction {
  type: typeof GENERATE_REPORT;
  payload: {
    reportJobType: ReportJobType;
    field_id: number | null;
    campaign_id: string;
    farmer_id: number;
    //*Force mode to asynchronous in this case
    mode: ProcessingExecutionMode;
  };
}

export type ReportActionTypes =
  | LoadReportsConfigAction
  | LoadReportsConfigSuccessAction
  | LoadReportsAction
  | LoadReportsSuccessAction
  | SetSelectedReportAction
  | RetryReportGenerationAction
  | GenerateNotificationAction
  | GetProducerReportJobSuccessAction
  | GetProducerReportJobSuccessAction
  | GetParentReportJobsAction
  | GetParentReportJobsSuccessAction
  | GenerateCampaignReportAction
  | DownloadZipFileAction
  | GenerateReportAction;

export const sharedReportActions = {
  generateReport: (
    reportJobType: ReportJobType,
    field_id: number | null,
    campaign_id: string,
    farmer_id: number,
    mode: ProcessingExecutionMode,
  ): GenerateReportAction => ({
    type: GENERATE_REPORT,
    payload: {
      reportJobType: reportJobType,
      field_id: field_id,
      campaign_id: campaign_id,
      farmer_id: farmer_id,
      mode: mode,
    },
  }),
  downloadFilesAsZip: (parentJobs: string[]): DownloadZipFileAction => ({
    type: DOWNLOAD_ZIP_FILE,
    payload: {
      parentJobs,
    },
  }),
  generateCampaignReports: (
    campaignId: string,
    type: ReportJobType,
    producers: string[],
    mode?: ProcessingExecutionMode,
  ): GenerateCampaignReportAction => ({
    type: GENERATE_CAMPAIGN_REPORT,
    payload: {
      campaignId,
      type,
      producers,
      mode,
    },
  }),
  getParentReportJobs: (
    campaignId: number,
    type: ReportJobType,
    startDate?: string,
    endDate?: string,
  ): GetParentReportJobsAction => ({
    type: GET_PARENT_REPORT_JOBS,
    payload: {
      campaignId,
      type,
      start_date: startDate,
      end_date: endDate,
    },
  }),
  getParentReportJobsSuccess: (data: {
    [key: string]: ParentReportJob;
  }): GetParentReportJobsSuccessAction => ({
    type: GET_PARENT_REPORT_JOBS_SUCCESS,
    payload: data,
  }),
  getProducerReportJob: (
    campaignId: number,
    type: ReportJobType,
    start_date: string,
    end_date: string,
  ): GetProducerReportJobAction => ({
    type: GET_PRODUCER_REPORT_JOB,
    payload: {
      campaignId,
      type,
      start_date,
      end_date,
    },
  }),
  getProducerReportJobSuccess: (data: {
    [producer_ref: string]: ProducerReportJob;
  }): GetProducerReportJobSuccessAction => ({
    type: GET_PRODUCER_REPORT_JOB_SUCCESS,
    payload: data,
  }),
  generateNotification: (reportJob: ReportJob): GenerateNotificationAction => ({
    type: GENERATE_NOTIFICATION,
    payload: {reportJob},
  }),
  retryReportGeneration: (reportId: string): RetryReportGenerationAction => ({
    type: RETRY_REPORT_GENERATION,
    payload: {reportId},
  }),
  loadReportsConfig: (): LoadReportsConfigAction => ({
    type: LOAD_REPORTS_CONFIG,
  }),

  loadReportsConfigsSuccess: (config: {
    [key: string]: ReportConfiguration;
  }): LoadReportsConfigSuccessAction => ({
    type: LOAD_REPORTS_CONFIG_SUCCESS,
    payload: config,
  }),

  loadReports: (
    campaignId: number,
    reportType: ReportJobType,
    start_date?: string,
    end_date?: string,
    producer_ref?: string,
    limit?: number,
    date?: Date,
  ): LoadReportsAction => ({
    type: LOAD_REPORTS,
    payload: {
      campaignId,
      type: reportType,
      start_date,
      end_date,
      producer_ref,
      limit,
      date,
    },
  }),

  loadReportsSuccess: (reports: {
    [key: string]: ReportJob;
  }): LoadReportsSuccessAction => ({
    type: LOAD_REPORTS_SUCCESS,
    payload: reports,
  }),

  setSelectedReport: (report: ReportJob) => ({
    type: SET_SELECTED_REPORT,
    payload: report,
  }),
};

export default sharedReportActions;
